import { getAcceptedAppSchemes } from './app-scheme';

const APP_LINK_HOSTS = new Set([
  'app.tabbio.com',
  'tabbio.com',
  'www.tabbio.com',
]);

export const NOTIFICATION_LIST_ORIGIN = 'notifications';

const JOBS_STATIC_ROUTES = new Set(['my-jobs', 'apply-browser']);

function normalizePath(path: string) {
  const [pathname = '/', search = ''] = path.split('?');
  const normalizedPathname = pathname.startsWith('/')
    ? pathname
    : `/${pathname}`;
  return `${normalizedPathname}${search ? `?${search}` : ''}`;
}

function normalizeJobTarget(path: string) {
  const [pathname = '/', search = ''] = path.split('?');
  if (pathname === '/jobs' || pathname === '/(app)/jobs') {
    return `/(app)/jobs${search ? `?${search}` : ''}`;
  }

  const match = pathname.match(/^\/(?:\(app\)\/)?jobs\/([^/]+)$/);
  if (!match?.[1]) {
    return null;
  }

  let jobId = match[1];
  try {
    jobId = decodeURIComponent(jobId);
  } catch {
    // Use the raw id if the push payload already contains an unencoded catalog id.
  }

  return `/(app)/jobs/${encodeURIComponent(jobId)}${search ? `?${search}` : ''}`;
}

function normalizeAppSectionTarget(path: string) {
  const [pathname = '/', search = ''] = path.split('?');
  const suffix = search ? `?${search}` : '';
  const aliases: Record<string, string> = {
    '/chat': '/(app)/chat',
    '/cv': '/(app)/cv',
    '/profile': '/(app)/profile',
    '/jobs/my-jobs': '/(app)/jobs/my-jobs',
  };
  return `${aliases[pathname] ?? pathname}${suffix}`;
}

function normalizeUrl(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('/')) {
    return normalizePath(trimmed);
  }

  try {
    const parsed = new URL(trimmed);
    const scheme = parsed.protocol.endsWith(':')
      ? parsed.protocol.slice(0, -1)
      : parsed.protocol;

    if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
      if (!APP_LINK_HOSTS.has(parsed.host)) return null;
      return normalizePath(`${parsed.pathname}${parsed.search}`);
    }

    if (getAcceptedAppSchemes().has(scheme)) {
      const route = parsed.host
        ? `/${parsed.host}${parsed.pathname}`
        : parsed.pathname;
      return normalizePath(`${route}${parsed.search}`);
    }
  } catch {
    return null;
  }

  return null;
}

function isAllowedTarget(path: string) {
  const pathname = path.split('?')[0] ?? path;
  return (
    pathname === '/(app)/chat' ||
    pathname === '/(app)/cv' ||
    pathname === '/(app)/profile' ||
    pathname === '/(app)/jobs/my-jobs' ||
    pathname === '/notifications' ||
    pathname === '/messages' ||
    pathname === '/jobs' ||
    pathname === '/(app)/jobs' ||
    pathname === '/auto-apply' ||
    pathname === '/services' ||
    pathname === '/settings' ||
    pathname === '/settings/reports' ||
    pathname === '/upgrade' ||
    pathname.startsWith('/messages/') ||
    pathname.startsWith('/jobs/') ||
    pathname.startsWith('/(app)/jobs/') ||
    pathname.startsWith('/subscription/return')
  );
}

function readNotificationCatalogJobId(
  payload: {
    catalogJobId?: unknown;
    externalJobPostingId?: unknown;
    jobId?: unknown;
  } | null,
) {
  if (
    typeof payload?.catalogJobId === 'string' &&
    payload.catalogJobId.trim()
  ) {
    return payload.catalogJobId.trim();
  }

  if (
    typeof payload?.externalJobPostingId === 'string' &&
    payload.externalJobPostingId.trim()
  ) {
    const id = payload.externalJobPostingId.trim();
    return id.startsWith('external:') ? id : `external:${id}`;
  }

  if (typeof payload?.jobId === 'string' && payload.jobId.trim()) {
    const id = payload.jobId.trim();
    return id.startsWith('native:') || id.startsWith('external:')
      ? id
      : `native:${id}`;
  }

  return null;
}

export function resolveNotificationTarget(data: unknown): string | null {
  const payload =
    data && typeof data === 'object'
      ? (data as {
          url?: unknown;
          kind?: unknown;
          type?: unknown;
          outcome?: unknown;
          runId?: unknown;
          catalogJobId?: unknown;
          externalJobPostingId?: unknown;
          jobId?: unknown;
          conversationId?: unknown;
          event?: unknown;
          siteId?: unknown;
        })
      : null;

  // A published site suspended or restored (the site moderation notice): it
  // carries the site and no url, and opens that site's settings page.
  if (
    (payload?.event === 'site_suspended' || payload?.event === 'site_restored') &&
    typeof payload.siteId === 'string' &&
    payload.siteId.trim()
  ) {
    return `/sites/${encodeURIComponent(payload.siteId.trim())}`;
  }

  // A task paused because its plan lapsed (nova_paused) opens the board, where
  // the paused task and its Resume action live. It never carries a run.
  if (
    payload?.type === 'nova_paused' ||
    (payload?.kind === 'nova' && payload.outcome === 'paused')
  ) {
    return '/auto-apply';
  }

  // Nova automation notifications (run needs approval / finished / failed) open
  // the automation board; a runId deep-links straight to that run's live panel.
  if (payload?.kind === 'nova') {
    const runId =
      typeof payload.runId === 'string' && payload.runId.trim()
        ? payload.runId.trim()
        : null;
    return runId
      ? `/auto-apply?runId=${encodeURIComponent(runId)}`
      : '/auto-apply';
  }

  const explicitUrl =
    typeof payload?.url === 'string' ? normalizeUrl(payload.url) : null;
  const normalizedExplicitUrl = explicitUrl
    ? (normalizeJobTarget(explicitUrl) ??
      normalizeAppSectionTarget(explicitUrl))
    : null;
  if (normalizedExplicitUrl && isAllowedTarget(normalizedExplicitUrl)) {
    const explicitJobId = getCatalogJobIdFromNotificationTarget(
      normalizedExplicitUrl,
    );
    const payloadJobId = readNotificationCatalogJobId(payload);
    if (
      explicitJobId &&
      !explicitJobId.startsWith('external:') &&
      !explicitJobId.startsWith('native:') &&
      payloadJobId
    ) {
      const [, search = ''] = normalizedExplicitUrl.split('?');
      return `/(app)/jobs/${encodeURIComponent(payloadJobId)}${
        search ? `?${search}` : ''
      }`;
    }
    return normalizedExplicitUrl;
  }

  const payloadJobId = readNotificationCatalogJobId(payload);
  if (payloadJobId) {
    return `/(app)/jobs/${encodeURIComponent(payloadJobId)}`;
  }

  if (
    typeof payload?.conversationId === 'string' &&
    payload.conversationId.trim()
  ) {
    return `/messages/${encodeURIComponent(payload.conversationId.trim())}`;
  }

  return null;
}

/**
 * Keeps the notification list as the deterministic back destination when a
 * notification crosses into the nested jobs stack. Other notification targets
 * retain their normal navigation behavior.
 */
export function withNotificationListOrigin(
  target: string | null,
): string | null {
  if (!target) return null;

  const [pathname = '/', search = ''] = target.split('?');
  if (!getCatalogJobIdFromNotificationTarget(target)) {
    return target;
  }

  const query = new URLSearchParams(search);
  query.set('from', NOTIFICATION_LIST_ORIGIN);
  return `${pathname}?${query.toString()}`;
}

export function getNotificationListBackTarget(
  origin: string | undefined,
): '/notifications' | null {
  return origin === NOTIFICATION_LIST_ORIGIN ? '/notifications' : null;
}

export function getCatalogJobIdFromNotificationTarget(
  target: string | null,
): string | null {
  if (!target) return null;

  const [pathname = '/'] = target.split('?');
  const match = pathname.match(/^\/(?:\(app\)\/)?jobs\/([^/]+)$/);
  if (!match?.[1] || JOBS_STATIC_ROUTES.has(match[1])) {
    return null;
  }

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}
